import React from 'react';
import { useTranslation } from 'localization';
import type { Adapter } from '../types';
import './chartAdapter.scss';
import VelocitySparklineAtom, { type VelocityDataPoint } from '../../components/atoms/VelocitySparkline/VelocitySparklineAtom';

/** v1 ships one reference implementation. `line`/`bar` are reserved enum values with no adapter yet. */
const SUPPORTED_CHART_KINDS = new Set(['sparkline']);

function toDataPoints(series: unknown): VelocityDataPoint[] {
  if (!Array.isArray(series)) return [];
  return series
    .map((point): VelocityDataPoint | null => {
      if (typeof point === 'number') return { score: point };
      if (point && typeof point === 'object' && typeof (point as { score?: unknown }).score === 'number') {
        const p = point as { score: number; timestamp?: string };
        return { score: p.score, timestamp: p.timestamp };
      }
      return null;
    })
    .filter((p): p is VelocityDataPoint => p !== null);
}

/**
 * `Chart` catalog entry adapter. `kind: 'sparkline'` renders via the existing
 * `VelocitySparklineAtom` (numeric series, matching the `Chart.series` "array of numeric points"
 * contract). Unknown/unimplemented kinds (`line`, `bar` — reserved) render a named fallback rather
 * than crash or silently drop the node (SC12).
 */
const ChartAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const kind = props.kind as string | undefined;
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as React.ReactNode) : undefined;

  if (!kind || !SUPPORTED_CHART_KINDS.has(kind)) {
    return (
      <div
        className="chart-adapter chart-adapter--unsupported"
        data-component-id={componentId}
        role="img"
        aria-label={t('space.render.unsupportedChartKind', { kind })}
      >
        {t('space.render.unsupportedChartKind', { kind })}
      </div>
    );
  }

  const series = ctx.resolveValue(props.series);
  const dataPoints = toDataPoints(series);
  const color = typeof props.color === 'string' ? props.color : undefined;

  return (
    <div className="chart-adapter" data-component-id={componentId} data-chart-kind={kind} aria-label={typeof label === 'string' ? label : undefined}>
      <VelocitySparklineAtom dataPoints={dataPoints} areaFill={Boolean(color)} />
    </div>
  );
};

export default ChartAdapter;
